import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { KibButtonNew } from '@chewy/kib-controls-react'

import type {
  InventoryOrder,
  InventoryOrderItem,
} from '~/api/graphql/generated/types'
import DialogNames from '~/constants/DialogNames'
import {
  createInventoryOrderItemReceipts,
  getInventoryOrderError,
  getInventoryOrdersIsUpdating,
} from '~/store/duck/inventoryOrders'
import useCloseAfterCreation from '~/utils/useCloseAfterCreation'
import useDialog from '~/utils/useDialog'

import { isReceivingDisabled } from '../utils'
import { useConfirmReceiveOverage } from './hooks/useConfirmReceiveOverage'
import { useDraftReceipts } from './hooks/useDraftReceipts'
import {
  type ReceiptFieldGroup,
  useReceiptFields,
} from './hooks/useReceiptFields'
import { OrderItemReceiptsList } from './OrderItemReceiptsList'
import { OrderItemReceiptsPageHeader } from './OrderItemReceiptsPageHeader'
import { OrderItemReceiptsSummary } from './OrderItemReceiptsSummary'

import styles from './OrderItemReceiptsPage.module.scss'

const getTotalReceiptQuanities = (receipts: ReceiptFieldGroup[]) => {
  const { totalQuantityDamaged, totalQuantityReceived } = receipts.reduce(
    (totals, { fields }) => {
      const quantity = fields.receivedQuantity.value || 0
      if (fields.damaged.value) {
        totals.totalQuantityDamaged += quantity
      } else {
        totals.totalQuantityReceived += quantity
      }
      return totals
    },
    { totalQuantityDamaged: 0, totalQuantityReceived: 0 },
  )

  return { totalQuantityDamaged, totalQuantityReceived }
}

interface OrderItemReceiptsPageProps {
  inventoryOrder: InventoryOrder
  inventoryOrderItem: InventoryOrderItem
}

export const OrderItemReceiptsPage = ({
  inventoryOrder,
  inventoryOrderItem,
}: OrderItemReceiptsPageProps) => {
  const { t } = useTranslation('Common')
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const error = useSelector(getInventoryOrderError)
  const { receiptFields } = useReceiptFields(inventoryOrderItem.receipts)
  const {
    draftReceiptFields,
    addDraftReceipt,
    deleteDraftReceipt,
    validateDraftReceipts,
    getDraftReceiptsForSubmission,
  } = useDraftReceipts()
  const confirmReceiveOverage = useConfirmReceiveOverage(inventoryOrderItem)

  const [openConfirmCloseDialog, closeConfirmCloseDialog] = useDialog(
    DialogNames.CONFIRM_CLOSE_DIALOG,
  )

  const { totalQuantityDamaged, totalQuantityReceived } =
    getTotalReceiptQuanities([...receiptFields, ...draftReceiptFields])

  const isViewOnly = isReceivingDisabled(
    inventoryOrder.status,
    inventoryOrderItem.status,
  )

  const navigateToOrderItemsPage = () => {
    navigate(`/admin/catalog/inventories/orders/${inventoryOrder.id}`)
  }

  const closeAfterSaving = useCloseAfterCreation(() => {
    if (!error) {
      navigateToOrderItemsPage()
    }
  }, getInventoryOrdersIsUpdating)

  const submitReceipts = () => {
    const receiptsToSave = getDraftReceiptsForSubmission()
    closeAfterSaving()
    dispatch(
      createInventoryOrderItemReceipts(
        inventoryOrder.id,
        inventoryOrderItem.id,
        receiptsToSave,
      ),
    )
  }

  const handleSaveDraftReceipts = () => {
    if (!draftReceiptFields.length || !validateDraftReceipts()) {
      return
    }

    const isReceivingOverage =
      totalQuantityReceived + totalQuantityDamaged >
      inventoryOrderItem.orderedQuantity

    if (isReceivingOverage) {
      confirmReceiveOverage({
        onConfirm: (proceed: boolean) => {
          if (proceed) {
            submitReceipts()
          }
        },
        totalQuantityOrdered: inventoryOrderItem.orderedQuantity,
        totalQuantityReceived: totalQuantityReceived + totalQuantityDamaged,
      })
      return
    }

    submitReceipts()
  }

  const handleCancel = () => {
    if (draftReceiptFields.length > 0) {
      openConfirmCloseDialog({
        // Save
        onOk: () => {
          handleSaveDraftReceipts()
          closeConfirmCloseDialog()
        },
        // Don't Save
        onNotOk: () => {
          navigateToOrderItemsPage()
          closeConfirmCloseDialog()
        },
      })
      return
    }
    navigateToOrderItemsPage()
  }

  useEffect(() => {
    const shouldCreateInitialDraftReceipt =
      !isViewOnly && !(receiptFields.length || draftReceiptFields.length)
    if (shouldCreateInitialDraftReceipt) {
      addDraftReceipt()
    }
  }, [isViewOnly, receiptFields, draftReceiptFields])

  const isSaveDisabled = isUpdating || !draftReceiptFields.length || isViewOnly

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <OrderItemReceiptsPageHeader
          inventoryOrder={inventoryOrder}
          inventoryOrderItem={inventoryOrderItem}
        />
        <div className={styles.receiptsList}>
          <OrderItemReceiptsList
            addDraftReceipt={addDraftReceipt}
            deleteDraftReceipt={deleteDraftReceipt}
            draftReceiptFields={draftReceiptFields}
            inventoryOrderItem={inventoryOrderItem}
            isViewOnly={isViewOnly}
            receiptFields={receiptFields}
          />
        </div>
        {isViewOnly && (
          <div className={styles.backButton}>
            <KibButtonNew onClick={navigateToOrderItemsPage}>
              {t('Common:GO_BACK')}
            </KibButtonNew>
          </div>
        )}
      </div>
      {!isViewOnly && (
        <div className={styles.footer}>
          <div className={styles.buttons}>
            <KibButtonNew
              disabled={isSaveDisabled}
              loading={isUpdating}
              onClick={handleSaveDraftReceipts}
            >
              {t('Common:SAVE_ACTION')}
            </KibButtonNew>
            <KibButtonNew emphasis="secondary" onClick={handleCancel}>
              {t('Common:CANCEL_ACTION')}
            </KibButtonNew>
          </div>
          <OrderItemReceiptsSummary
            inventoryOrderItem={inventoryOrderItem}
            orderedQuantity={inventoryOrderItem.orderedQuantity}
            totalQuantityDamaged={totalQuantityDamaged}
            totalQuantityReceived={totalQuantityReceived}
          />
        </div>
      )}
    </div>
  )
}
